import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft,
  Loader,
  Search,
  CheckCircle2,
  AlertTriangle,
  X,
  ChevronDown,
  ChevronRight,
  PenLine,
} from 'lucide-react';
import { CREW_PHASES, CREW_PHASE_LABEL, phaseOf } from '../lib/dispatch-crew-phase';
import type { CrewPhaseValue } from '../lib/dispatch-crew-phase';
import { jobHasSeparateWashDay } from '../lib/dispatch-phase-cards';
import { hasWashDate } from '../lib/job-phase-dates';
import { defaultPhaseForSpecialty } from '../lib/crew-specialty';
import type { CrewPhase } from '../types/dispatch';
import { formatCalendarDate } from '../lib/calendar-date';
import { usePositions } from '../hooks/usePositions';
import { heldPositionIds } from '../types/position';
import { unwrapArray } from '../lib/api-envelope';
import { readJobPositions, jobPositionText } from '../lib/job-crew-positions';
import type { CrewPatchBody } from '../lib/dispatch-crew-phase';
import { Tooltip } from '../components/ui/tooltip';

// ─── Cert badge styles ──────────────────────────────────────────────────────
const CERT_STYLES: Record<string, { label: string; color: string }> = {
  H2S_alive: { label: 'H2S Alive', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  CSTS: { label: 'CSTS', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  standard_first_aid: { label: 'First Aid', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  ground_disturbance: { label: 'Ground Dist.', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  WHMIS: { label: 'WHMIS', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
  OSHA_10: { label: 'OSHA 10', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  OSHA_30: { label: 'OSHA 30', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  H2S_awareness: { label: 'H2S Awareness', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  first_aid_CPR: { label: 'First Aid/CPR', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  other: { label: 'Other', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
};

const AVAILABILITY_BADGE: Record<string, { label: string; className: string }> = {
  assigned: { label: 'Assigned', className: 'bg-fieldiq-accent/20 text-fieldiq-accent' },
  available: { label: 'Available', className: 'bg-green-500/20 text-green-400' },
  busy: { label: 'Busy', className: 'bg-amber-500/20 text-amber-400' },
  time_off: { label: 'On Holiday', className: 'bg-red-500/20 text-red-400' },
};

/**
 * Roles the API's PATCH /dispatch/jobs/:id/crew accepts (requireRole). Field
 * workers and drivers get a 403, so they are not offered the control at all.
 */
const CREW_EDIT_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher'];

/** A job-position slot: the whole job, or one phase of a split job (API 0426). */
type PositionSlot = 'all' | 'wash' | 'run';
const NO_POSITION_GROUP = 'No position set';

export default function CrewAssignmentScreen() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  /**
   * The job's designated field-ticket writer. Job-scoped scalar, not a
   * per-member flag — the API clears the previous holder when a new one is set,
   * so a single id (or null for "nobody") is the only representable state.
   */
  const [ticketWriterId, setTicketWriterId] = useState<string | null>(null);
  /**
   * The phase each hand works, as the screen currently shows it. Always one of
   * the three words — a saved NULL seeds 'both', because NULL behaves as every
   * phase everywhere it is read. `serverPhase` below is what makes that
   * seeding safe: a hand still showing the seeded value is UNCHANGED and is
   * not sent, so nobody is re-written (and nobody is re-added, and nobody is
   * texted) for a value they already effectively had.
   */
  const [phaseById, setPhaseById] = useState<Record<string, CrewPhase>>({});
  /**
   * Job positions (API 0426) the dispatcher has PICKED, per hand per slot.
   * `''` is a pick of "no job position". Like `phaseById`, an untouched slot has
   * no entry and reads from the server, so opening the screen and saving writes
   * nothing for anybody.
   */
  const [positionPicks, setPositionPicks] = useState<Record<string, Partial<Record<PositionSlot, string>>>>({});
  /**
   * `warnings[]` from the last save: a position the hand is not listed for was
   * ACCEPTED, and the API names it. The save landed, so this is not an error —
   * but it is not a success to wave through in a green toast either. Shown
   * inline and the screen stays put; Save & Continue again moves on.
   */
  const [saveWarnings, setSaveWarnings] = useState<string[]>([]);

  const canDesignate = CREW_EDIT_ROLES.includes(user?.role);

  /** Every active position of the tenant — the "Other positions…" half. */
  const { data: allPositions } = usePositions();

  /**
   * Each hand's "can do" list (user_positions, API 0425), primary first. Only
   * GET /users carries it; available-crew does not. A failed read leaves the
   * primary (from available-crew) as the only "can do" entry and every other
   * position under "Other positions…" — still every option, just less sorted.
   */
  const { data: canDoById } = useQuery({
    queryKey: ['users', 'position-sets'],
    queryFn: () => api.users.list().then(r => {
      const map = new Map<string, string[]>();
      for (const u of unwrapArray<any>(r.data)) {
        if (u && typeof u.id === 'string') map.set(u.id, heldPositionIds(u) ?? []);
      }
      return map;
    }),
    enabled: canDesignate,
    refetchOnWindowFocus: false,
  });

  const { data: crew, isLoading, refetch } = useQuery({
    queryKey: ['available-crew', jobId],
    queryFn: () => api.dispatch.getAvailableCrew(jobId!).then(r => r.data.data ?? []),
    enabled: !!jobId,
    refetchOnWindowFocus: false,
  });

  /**
   * The job itself, for two things `available-crew` does not carry: the wash
   * and run dates that decide whether there are two phases to choose between,
   * and each seated hand's saved `phase` (API #730). Same key as JobDetail's,
   * so the invalidation this screen already fires on save refreshes both.
   */
  const { data: job } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => api.jobs.get(jobId!).then(r => r.data.data),
    enabled: !!jobId,
  });

  /**
   * Whether the job has two phases a hand could work one of. A wash sharing
   * the run's day is one day's work for one crew, so there is nothing to
   * choose and the screen offers nothing — exactly the board's rule, read from
   * the board's own helper rather than restated here.
   */
  const splitPhases = !!job && jobHasSeparateWashDay(job);

  /**
   * Whether the Wash / Run / Both picker shows: whenever the job has a wash
   * DATE — a same-day wash included, because the wash crew often goes out
   * first and a wash-only hand must not be texted the run (TNT J-4404). It
   * is hidden only with no wash date, where there is one phase and nothing to
   * choose; there no phase is saved at all (`phase: null`, no `phases`).
   *
   * Job POSITIONS keep `splitPhases` above (a slot per phase only when the
   * wash is a day of its own) — that is API 0426's rule, not this one.
   */
  const phaseChoice = !!job && hasWashDate(job);

  /** Each hand's specialty, from available-crew — the pre-fill's input. */
  const specialtyById = useMemo(() => {
    const map = new Map<string, unknown>();
    for (const m of (crew ?? []) as any[]) {
      if (m && typeof m.id === 'string') map.set(m.id, m.specialty);
    }
    return map;
  }, [crew]);

  /** What the database says today, id → phase. Absent id = not on the crew. */
  const serverPhases = useMemo(() => {
    const map = new Map<string, CrewPhaseValue>();
    for (const m of (job?.crew ?? []) as any[]) {
      if (m && typeof m.id === 'string') map.set(m.id, phaseOf(m));
    }
    return map;
  }, [job]);

  /**
   * The value a hand's control shows before anyone touches it.
   *
   * A SEATED hand shows their saved phase — never overwritten by the pre-fill;
   * a saved NULL reads Both (it behaves as every phase).
   *
   * A hand the office TICKS (no saved row yet) is pre-filled from their
   * specialty: a thread washer Wash, everyone else Run, blank and unknown
   * included (`defaultPhaseForSpecialty`, the one list in crew-specialty.ts).
   * It is only a pre-fill — shown on the row, changeable to Wash, Run or Both
   * before saving — and nobody is hidden by specialty.
   */
  const seededPhase = (id: string): CrewPhase =>
    serverPhases.has(id) ? serverPhases.get(id) ?? 'both' : defaultPhaseForSpecialty(specialtyById.get(id));
  const phaseFor = (id: string): CrewPhase => phaseById[id] ?? seededPhase(id);
  /**
   * A SEATED hand saved as NULL and not touched since — shown as Both, and
   * said to be unset where it is shown. A hand not yet on the crew is not
   * "unset": they default to Both and that default is written on save.
   */
  const phaseUnset = (id: string): boolean =>
    phaseById[id] === undefined && serverPhases.has(id) && serverPhases.get(id) == null;

  /**
   * Baseline: whatever the server currently says. React Query's structural
   * sharing keeps this reference stable when a refetch returns identical data,
   * so in-progress edits survive a no-op refetch — but a real change (including
   * the refetch we fire after a failed save) resyncs the local state, which is
   * how a rolled-back designation gets rolled back in the UI too.
   */
  useEffect(() => {
    if (!crew) return;
    setSelectedIds(new Set<string>(
      crew.filter((m: any) => m.already_assigned).map((m: any) => m.id as string)
    ));
    setTicketWriterId(crew.find((m: any) => m.is_ticket_writer)?.id ?? null);
    // Phase edits are dropped with the rest of the local state. Nothing is
    // seeded in their place: an untouched hand has no entry here and reads
    // from the server value, so "untouched" stays distinguishable from
    // "picked, and happens to match".
    setPhaseById({});
    setPositionPicks({});
  }, [crew]);

  /** What the database says today: id → that hand's job-position rows. */
  const serverPositions = useMemo(() => {
    const map = new Map<string, ReturnType<typeof readJobPositions>>();
    for (const m of (crew ?? []) as any[]) {
      if (m && typeof m.id === 'string') map.set(m.id, readJobPositions(m.positions));
    }
    return map;
  }, [crew]);

  /** The slots a hand's position is picked in: one per phase they work on a
   *  split job, or the whole job. */
  const slotsFor = (id: string): PositionSlot[] => {
    if (!splitPhases) return ['all'];
    const p = phaseFor(id);
    return p === 'both' ? ['wash', 'run'] : [p];
  };

  /**
   * The saved value a slot shows before anyone touches it. A phase slot falls
   * back to a whole-job row, which covers every phase. The whole-job slot shows
   * a per-phase pair only when both phases hold the same position — two
   * different ones cannot be shown in one select, and `savedPositionNote`
   * says what is saved instead.
   */
  const seededPosition = (id: string, slot: PositionSlot): string => {
    const rows = serverPositions.get(id) ?? [];
    const whole = rows.find(r => r.phase === null);
    if (slot !== 'all') return (rows.find(r => r.phase === slot) ?? whole)?.positionId ?? '';
    if (whole) return whole.positionId;
    const ids = new Set(rows.map(r => r.positionId));
    return ids.size === 1 ? rows[0].positionId : '';
  };
  const positionFor = (id: string, slot: PositionSlot): string =>
    positionPicks[id]?.[slot] ?? seededPosition(id, slot);

  const serverTicketWriterId: string | null = useMemo(
    () => crew?.find((m: any) => m.is_ticket_writer)?.id ?? null,
    [crew],
  );

  const ticketWriterName = useMemo(() => {
    if (!ticketWriterId || !crew) return null;
    const m = crew.find((x: any) => x.id === ticketWriterId);
    return m ? [m.first_name, m.last_name].filter(Boolean).join(' ') : null;
  }, [ticketWriterId, crew]);

  const filteredCrew = useMemo(() => {
    if (!crew) return [];
    if (!search.trim()) return crew;
    const q = search.toLowerCase();
    return crew.filter((m: any) => {
      const name = `${m.first_name || ''} ${m.last_name || ''}`.toLowerCase();
      return name.includes(q) || (m.initials || '').toLowerCase().includes(q);
    });
  }, [crew, search]);

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  /**
   * Grouped by the hand's PRIMARY position, by the tenant's own name for it.
   * This read `member.position`, which available-crew has never sent, so every
   * hand landed in one "Other" group; and the order was a hardcoded list of one
   * tenant's position names. Alphabetical now, hands with no position last.
   */
  const groupedCrew = useMemo(() => {
    const groupMap = new Map<string, any[]>();
    for (const member of filteredCrew) {
      const pos = (member.position_name || '').trim() || NO_POSITION_GROUP;
      if (!groupMap.has(pos)) groupMap.set(pos, []);
      groupMap.get(pos)!.push(member);
    }

    // Sort members alphabetically within each group
    Array.from(groupMap.values()).forEach(items => {
      items.sort((a: any, b: any) => {
        const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
        const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
    });

    const groups: { position: string; items: any[] }[] = Array.from(groupMap.entries())
      .filter(([pos]) => pos !== NO_POSITION_GROUP)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([position, items]) => ({ position, items }));
    if (groupMap.has(NO_POSITION_GROUP)) {
      groups.push({ position: NO_POSITION_GROUP, items: groupMap.get(NO_POSITION_GROUP)! });
    }

    return groups;
  }, [filteredCrew]);

  const toggleGroup = (position: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(position)) next.delete(position);
      else next.add(position);
      return next;
    });
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    // Taking someone off the crew also gives up their designation — the API
    // would 400 on a ticketWriter that is being removed in the same request.
    setTicketWriterId(prev => (prev === id && selectedIds.has(id) ? null : prev));
  };

  const saveMutation = useMutation({
    mutationFn: async (): Promise<string[]> => {
      if (!crew || !jobId) return [];
      const originalAssigned = new Set<string>(
        crew.filter((m: any) => m.already_assigned).map((m: any) => m.id as string)
      );
      const add = Array.from(selectedIds).filter(id => !originalAssigned.has(id));
      const remove = Array.from(originalAssigned).filter(id => !selectedIds.has(id));
      const writerChanged = canDesignate && ticketWriterId !== serverTicketWriterId;

      /**
       * Phase changes on hands who are ALREADY on the crew. Sent as `phases`,
       * never as `add` — `add` fires the assignment notification and the SMS,
       * and a dispatcher moving a seated hand from Both to Wash has not
       * assigned them to anything. Only hands whose shown value differs from
       * the saved one are here; a NULL hand left reading Both is unchanged and
       * writes nothing.
       */
      const phases = phaseChoice
        ? Array.from(selectedIds)
            .filter(id => originalAssigned.has(id) && phaseFor(id) !== seededPhase(id))
            .map(id => ({ userId: id, phase: phaseFor(id) }))
        : [];

      /**
       * Job positions (API 0426), for hands staying on (or joining) the crew.
       * A hand is sent only if one of their shown slots differs from the saved
       * value — and then ALL their shown slots are sent, because a per-phase
       * write deletes a whole-job row (the API keeps the two exclusive) and the
       * other phase must not lose its position as a side effect. A removed hand
       * sends nothing: the API deletes their positions with the row.
       */
      const positions: NonNullable<CrewPatchBody['positions']> = [];
      if (canDesignate) {
        for (const id of Array.from(selectedIds)) {
          const slots = slotsFor(id);
          if (!slots.some(slot => positionFor(id, slot) !== seededPosition(id, slot))) continue;
          for (const slot of slots) {
            positions.push({
              userId: id,
              phase: slot === 'all' ? null : slot,
              positionId: positionFor(id, slot) || null,
            });
          }
        }
      }

      if (add.length === 0 && remove.length === 0 && phases.length === 0 && positions.length === 0 && !writerChanged) {
        return [];
      }

      // ONE-PASS CONTRACT — the whole reason a fresh pick can be designated
      // without a second visit. PATCH /dispatch/jobs/:id/crew applies
      // remove → add → clear-designation → set-designation inside ONE
      // transaction, so a userId that appears in `add` is already on the crew
      // by the time the designation UPDATE runs. Sending both keys together is
      // therefore correct, and the ORDER OF THE KEYS IN THIS OBJECT IS NOT WHAT
      // MAKES IT WORK — the server's statement order is. Verified against the
      // real handler on an ephemeral Postgres: {add:[u], ticketWriter:u} for a
      // hand with no crew row returns 200 with is_ticket_writer true.
      //
      // The 400 ('not on this job's crew') is still live and still correct: it
      // fires for a userId that is neither on the crew nor in `add`. The UI
      // cannot produce that, because the control only renders for a selected
      // member and every selected member is either already assigned or in `add`.
      // Do NOT re-add an `already_assigned` gate to the control — that was the
      // two-visit bug, and it was over-strict, not load-bearing.
      const body: CrewPatchBody & { ticketWriter?: string | null } = {};
      // The object form of `add`, so a new hand is seated and their phase is
      // stated in the same transaction (the handler applies phases after the
      // insert, on purpose). `phase: null` on a job with one day's work is the
      // honest value — not stated, which every phase matches — rather than a
      // 'both' that would read as a decision somebody made.
      if (add.length > 0) {
        body.add = add.map(id => ({ userId: id, phase: phaseChoice ? phaseFor(id) : null }));
      }
      if (remove.length > 0) body.remove = remove;
      if (phases.length > 0) body.phases = phases;
      if (positions.length > 0) body.positions = positions;
      // Omitted when unchanged — the API leaves the designation untouched.
      // Sent as null to clear it, or as a userId to move it.
      if (writerChanged) body.ticketWriter = ticketWriterId;
      const res = await api.dispatch.updateCrew(jobId, body);
      const warnings = res?.data?.warnings;
      return Array.isArray(warnings) ? warnings.filter((w: unknown): w is string => typeof w === 'string') : [];
    },
    onMutate: () => setSaveWarnings([]),
    onSuccess: (warnings) => {
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['dispatch-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['available-crew', jobId] });
      // Saved, with something worth reading first — stay on the screen.
      if (warnings.length > 0) {
        setSaveWarnings(warnings);
        return;
      }
      navigate(`/jobs/${jobId}/assign-equipment`);
    },
    onError: async (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to save crew changes');
      // The API rolls the whole request back on a 400, so nothing was applied.
      // Pull server truth back down rather than leaving the UI showing a
      // designation (or an add/remove) that did not persist.
      await refetch();
    },
  });

  return (
    <div className="animate-fade-in min-h-full pb-[var(--bottom-chrome)] bg-fieldiq-dark flex flex-col">
      {/* Header */}
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <div>
            <h1 className="text-base font-bold text-foreground">Assign Crew</h1>
            <p className="text-xs text-fieldiq-muted">Step 1 of 2</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="px-4 pt-3 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fieldiq-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search crew..."
            className="w-full bg-fieldiq-card border border-fieldiq-border rounded-lg pl-10 pr-8 py-2.5 text-sm text-white placeholder:text-fieldiq-muted focus:outline-none focus:ring-1 focus:ring-fieldiq-accent"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-fieldiq-muted">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Field ticket writer — job-scoped, optional, one at a time */}
      {canDesignate && (
        <div className="px-4 pb-2">
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white flex items-center gap-2">
                  <PenLine className="w-4 h-4 text-fieldiq-accent flex-shrink-0" />
                  Field Ticket Writer
                </p>
                <p className="text-xs text-fieldiq-muted mt-1">
                  Pick one crew member who can create and edit field tickets for this job.
                  Picking someone new replaces the previous pick. This is optional — leave it
                  empty and field tickets work exactly as they do now.
                </p>
                {/* Reflects the LIVE selection, not the saved crew. With nobody
                    selected the per-row control does not exist yet, so saying
                    "Nobody picked" there announced a control the screen was not
                    showing; name the next action instead. */}
                <p className="text-xs mt-1.5">
                  {ticketWriterName ? (
                    <span className="text-fieldiq-accent font-semibold">{ticketWriterName}</span>
                  ) : selectedIds.size === 0 ? (
                    <span className="text-fieldiq-muted">
                      Pick your crew below — then choose one of them here.
                    </span>
                  ) : (
                    <span className="text-fieldiq-muted">
                      Nobody picked — tap <span className="text-fieldiq-text font-semibold">Field ticket writer</span> under
                      {' '}{selectedIds.size === 1 ? 'the selected crew member' : `any of the ${selectedIds.size} selected crew`}.
                    </span>
                  )}
                </p>
              </div>
              {ticketWriterId && (
                <button
                  onClick={() => setTicketWriterId(null)}
                  className="flex-shrink-0 text-xs font-semibold text-fieldiq-muted hover:text-white border border-fieldiq-border rounded-lg px-3 py-1.5 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Crew List */}
      <div className="flex-1 overflow-y-auto px-4 pb-36">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader size={28} className="animate-spin text-fieldiq-accent" />
          </div>
        ) : filteredCrew.length === 0 ? (
          <p className="text-fieldiq-muted text-sm text-center py-8">
            {search ? 'No crew match your search' : 'No active employees found'}
          </p>
        ) : (
          <div className="space-y-1">
            {groupedCrew.map(({ position, items }) => {
              const isCollapsed = collapsedGroups.has(position);
              return (
                <div key={position}>
                  {/* Group header — matches equipment step */}
                  <button
                    onClick={() => toggleGroup(position)}
                    className="sticky top-0 z-10 w-full flex items-center gap-2 px-2 py-2 bg-fieldiq-dark text-left"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="w-4 h-4 text-fieldiq-muted flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-fieldiq-muted flex-shrink-0" />
                    )}
                    <span className="text-sm font-semibold text-white">{position}</span>
                    <span className="text-xs text-fieldiq-muted">({items.length})</span>
                  </button>

                  {/* Group items */}
                  {!isCollapsed && (
                    <div className="space-y-2 pb-2">
                      {items.map((member: any) => {
                        const isSelected = selectedIds.has(member.id);
                        const badge = AVAILABILITY_BADGE[member.availability] || AVAILABILITY_BADGE.available;
                        const initials = member.initials || `${(member.first_name?.[0] || '').toUpperCase()}${(member.last_name?.[0] || '').toUpperCase()}`;
                        const fullName = [member.first_name, member.last_name].filter(Boolean).join(' ');
                        const certs: any[] = member.certifications ?? [];
                        const busyJobs: any[] = member.active_jobs ?? [];
                        const timeOff: any[] = member.time_off ?? [];

                        const isTicketWriter = ticketWriterId === member.id;

                        return (
                          <div
                            key={member.id}
                            className={`w-full rounded-xl px-4 py-3 border transition-colors ${
                              isSelected
                                ? 'bg-fieldiq-accent/10 border-fieldiq-accent/40'
                                : 'bg-fieldiq-card border-fieldiq-border'
                            }`}
                          >
                          <button
                            type="button"
                            onClick={() => toggleSelect(member.id)}
                            // Without a name, the accessible label is the whole card
                            // read aloud — name, badge, cert list, every warning.
                            aria-pressed={isSelected}
                            aria-label={`Crew member: ${fullName}`}
                            className="w-full flex items-start gap-3 text-left"
                          >
                            {/* Checkbox */}
                            <div className="mt-0.5 flex-shrink-0">
                              {isSelected ? (
                                <CheckCircle2 className="w-5 h-5 text-fieldiq-accent" />
                              ) : (
                                <div className="w-5 h-5 rounded-full border-2 border-fieldiq-border" />
                              )}
                            </div>

                            {/* Initials avatar */}
                            <span className="w-10 h-10 bg-fieldiq-accent/20 text-fieldiq-accent rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                              {initials}
                            </span>

                            {/* Info */}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-white text-sm font-semibold truncate">{fullName}</p>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.className}`}>
                                  {badge.label}
                                </span>
                              </div>
                              {/* The PRIMARY position, by the tenant's name for it. */}
                              <p className="text-fieldiq-muted text-xs mt-0.5">
                                {member.position_name || '—'}
                              </p>

                              {/* Time-off warning */}
                              {timeOff.length > 0 && (
                                <div className="mt-1.5 flex items-start gap-1.5 text-red-400 text-xs">
                                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                  <span>On {timeOff[0].time_off_type?.replace(/_/g, ' ')} — {formatCalendarDate(timeOff[0].start_date, { month: 'short', day: 'numeric' }) ?? '—'} to {formatCalendarDate(timeOff[0].end_date, { month: 'short', day: 'numeric' }) ?? '—'}</span>
                                </div>
                              )}

                              {/* Busy warning */}
                              {member.availability === 'busy' && busyJobs.length > 0 && (
                                <div className="mt-1.5 flex items-start gap-1.5 text-amber-400 text-xs">
                                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                  <span>Also on: {busyJobs.map((j: any) => j.job_number).join(', ')}</span>
                                </div>
                              )}

                              {/* Cert warnings */}
                              {member.warnings?.filter((w: string) => w.includes('cert')).map((w: string, i: number) => (
                                <div key={i} className="mt-1.5 flex items-start gap-1.5 text-red-400 text-xs">
                                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                  <span>{w}</span>
                                </div>
                              ))}

                              {/* Certifications */}
                              {certs.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {certs.map((cert: any, ci: number) => {
                                    const style = CERT_STYLES[cert.type] || CERT_STYLES.other;
                                    return (
                                      <span
                                        key={ci}
                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${style.color}`}
                                      >
                                        {style.label}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </button>

                          {/* Wash / Run / Both — whenever the job has a wash
                              date, same-day included (see `phaseChoice`). With
                              no wash date there is only the run, and the
                              control would be a question with one answer. */}
                          {phaseChoice && isSelected && (
                            <div className="mt-2.5 pt-2.5 border-t border-fieldiq-border/60 pl-8">
                              <p className="text-[11px] font-semibold text-fieldiq-muted mb-1.5">
                                Works
                              </p>
                              <div
                                role="radiogroup"
                                aria-label={`Phase: ${fullName}`}
                                className="flex items-center gap-1.5"
                              >
                                {CREW_PHASES.map((p) => {
                                  const active = phaseFor(member.id) === p;
                                  return (
                                    <button
                                      key={p}
                                      type="button"
                                      role="radio"
                                      aria-checked={active}
                                      // Each row draws the same three words, so
                                      // without the name a screen reader hears
                                      // "Wash" N times with nothing saying whose.
                                      aria-label={`${CREW_PHASE_LABEL[p]}: ${fullName}`}
                                      onClick={() => setPhaseById(prev => ({ ...prev, [member.id]: p }))}
                                      className={`rounded-lg px-3 py-1.5 text-xs font-semibold border transition-colors ${
                                        active
                                          ? 'bg-fieldiq-accent/20 border-fieldiq-accent/50 text-fieldiq-accent'
                                          : 'border-fieldiq-border text-fieldiq-muted hover:text-white'
                                      }`}
                                    >
                                      {CREW_PHASE_LABEL[p]}
                                    </button>
                                  );
                                })}
                              </div>
                              {phaseUnset(member.id) && (
                                <p className="text-[11px] text-fieldiq-muted mt-1.5">
                                  Not set — every phase.
                                </p>
                              )}
                            </div>
                          )}

                          {/* Position ON THIS JOB (API 0426) — one select per
                              phase the hand works on a split job, else one for
                              the whole job. The hand's "can do" positions
                              first; every other active position after. */}
                          {canDesignate && isSelected && (() => {
                            const canDoIds: string[] = canDoById?.get(member.id) ?? heldPositionIds(member) ?? [];
                            const active = allPositions ?? [];
                            const canDo = canDoIds
                              .map(pid => active.find(p => p.id === pid))
                              .filter((p): p is NonNullable<typeof p> => !!p);
                            const other = active.filter(p => !canDoIds.includes(p.id));
                            const saved = serverPositions.get(member.id) ?? [];
                            const slots = slotsFor(member.id);
                            const savedText = jobPositionText(saved);
                            const savedHidden = slots.length === 1 && slots[0] === 'all'
                              && saved.length > 0 && seededPosition(member.id, 'all') === '';
                            return (
                              <div className="mt-2.5 pt-2.5 border-t border-fieldiq-border/60 pl-8">
                                <p className="text-[11px] font-semibold text-fieldiq-muted mb-1.5">
                                  Position on this job
                                </p>
                                <div className="flex flex-wrap items-center gap-2">
                                  {slots.map(slot => {
                                    const value = positionFor(member.id, slot);
                                    // A saved position that has since been
                                    // deactivated is not in the list; a select
                                    // with no option for its value shows the
                                    // first option instead, so give it one.
                                    const orphan = value && !active.some(p => p.id === value)
                                      ? saved.find(r => r.positionId === value)
                                      : undefined;
                                    const phaseWord = slot === 'all' ? null : CREW_PHASE_LABEL[slot];
                                    return (
                                      <label key={slot} className="flex items-center gap-1.5 text-xs text-fieldiq-muted">
                                        {phaseWord && <span>{phaseWord}</span>}
                                        <select
                                          aria-label={`Position on this job${phaseWord ? ` (${phaseWord})` : ''}: ${fullName}`}
                                          value={value}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            setPositionPicks(prev => ({ ...prev, [member.id]: { ...prev[member.id], [slot]: v } }));
                                          }}
                                          className="bg-fieldiq-card border border-fieldiq-border rounded-lg px-2 py-1.5 text-xs text-fieldiq-text focus:outline-none focus:ring-1 focus:ring-fieldiq-accent"
                                        >
                                          <option value="">No job position</option>
                                          {canDo.length > 0 && (
                                            <optgroup label="Can do">
                                              {canDo.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                            </optgroup>
                                          )}
                                          {other.length > 0 && (
                                            <optgroup label="Other positions…">
                                              {other.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                            </optgroup>
                                          )}
                                          {orphan && <option value={orphan.positionId}>{orphan.positionName ?? '—'}</option>}
                                        </select>
                                      </label>
                                    );
                                  })}
                                </div>
                                {savedHidden && savedText && (
                                  <p className="text-[11px] text-fieldiq-muted mt-1.5">
                                    Saved per phase: {savedText}
                                  </p>
                                )}
                              </div>
                            );
                          })()}

                          {/* Field ticket writer — radio-style, one per job.
                              Shown for anyone selected, whether or not they are
                              saved to the job yet. See ONE-PASS note above. */}
                          {canDesignate && isSelected && (
                            <div className="mt-2.5 pt-2.5 border-t border-fieldiq-border/60 pl-8">
                              <Tooltip content="Can create and edit field tickets for this job">
                                <button
                                  type="button"
                                  role="radio"
                                  aria-checked={isTicketWriter}
                                  // Every row renders a control reading "Field ticket
                                  // writer"; without the name, a screen reader hears the
                                  // same label N times with nothing saying whose row it is.
                                  aria-label={`Field ticket writer: ${fullName}`}
                                  onClick={() => setTicketWriterId(isTicketWriter ? null : member.id)}
                                  className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-semibold border transition-colors ${
                                    isTicketWriter
                                      ? 'bg-fieldiq-accent/20 border-fieldiq-accent/50 text-fieldiq-accent'
                                      : 'border-fieldiq-border text-fieldiq-muted hover:text-white'
                                  }`}
                                >
                                  <span
                                    className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                      isTicketWriter ? 'border-fieldiq-accent' : 'border-fieldiq-border'
                                    }`}
                                  >
                                    {isTicketWriter && (
                                      <span className="w-1.5 h-1.5 rounded-full bg-fieldiq-accent" />
                                    )}
                                  </span>
                                  <PenLine className="w-3.5 h-3.5" />
                                  Field ticket writer
                                </button>
                              </Tooltip>
                              {isTicketWriter && (
                                <p className="text-[11px] text-fieldiq-muted mt-1.5">
                                  Can create and edit field tickets for this job.
                                </p>
                              )}
                            </div>
                          )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Saved, with position warnings — inline and amber, never a green toast.
          The ink stays --fieldiq-text: an amber hue is a fill, not an ink. */}
      {saveWarnings.length > 0 && (
        <div className="px-4">
          <div
            role="alert"
            data-testid="crew-position-warnings"
            className="max-w-[1920px] mx-auto rounded-lg border border-fieldiq-border border-l-4 border-l-fi-amber bg-fi-amber/10 px-4 py-3"
          >
            <p className="text-sm font-semibold text-fieldiq-text flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-fieldiq-accent-signal flex-shrink-0" aria-hidden="true" />
              Saved — check these positions
            </p>
            <ul className="mt-1.5 space-y-1 text-xs text-fieldiq-text list-disc pl-6">
              {saveWarnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
            <p className="text-xs text-fieldiq-muted mt-1.5">
              Change a position and save again, or Save &amp; Continue to move on.
            </p>
          </div>
        </div>
      )}

      {/* Bottom Action Bar — pb-44 on mobile lifts buttons clear of the bottom nav (md:hidden) and the AI FAB */}
      <div className="border-t border-fieldiq-border px-4 pt-3 pb-44 mt-4 sm:pb-3">
        <div className="max-w-[1920px] mx-auto flex gap-3">
          <button
            onClick={() => navigate(`/jobs/${jobId}/assign-equipment`)}
            className="flex-1 py-3 rounded-lg border border-fieldiq-border text-fieldiq-text font-semibold text-sm hover:bg-fieldiq-border/30 transition-colors"
          >
            Skip
          </button>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="flex-1 bg-fieldiq-accent text-fieldiq-dark font-bold py-3 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 hover:bg-amber-600 transition-colors"
          >
            {saveMutation.isPending ? (
              <><Loader size={18} className="animate-spin" /> Saving...</>
            ) : (
              'Save & Continue'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
